import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Container,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import RefreshIcon from '@mui/icons-material/Refresh';
import BankTransactions from '../../collections/bankTransactions';
import BankTransaction from '../../models/bankTransaction';
import {
  bankTransactionStatusTranslations,
  getBankTransactionStatusColor,
  bankTransactionTypeTranslations,
  getBankTransactionTypeColor
} from '../../utils/bankTransactionTranslations';
import { jwtDecode } from "jwt-decode";
import { testBankTransactionsApi, validateApiResponse } from '../../utils/apiTest';

const BankTransactionsList = observer(({ setPageTitle }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || '');
  const [selectedType, setSelectedType] = useState(searchParams.get('transaction_type') || '');
  const [loading, setLoading] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [apiTestResult, setApiTestResult] = useState(null);

  // Initialize collection in state
  const [bankTransactions] = useState(() => new BankTransactions());

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle('Quản lý giao dịch ngân hàng');
    }
  }, [setPageTitle]);

  // Check if user is admin
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
        if (decodedToken.role !== 'ADMIN') {
          navigate('/');
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        setError("Lỗi xác thực. Vui lòng đăng nhập lại.");
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    setDebugInfo(null);

    try {
      const params = {
        status: selectedStatus,
        transaction_type: selectedType
      };

      // Use component level bankTransactions instance
      await bankTransactions.fetch(page + 1, rowsPerPage, params);

      if (bankTransactions.error) {
        setError(bankTransactions.error);
      }

    } catch (error) {
      console.error("Error fetching bank transactions:", error);
      setError("Lỗi khi tải dữ liệu. Vui lòng thử lại sau.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (newPage + 1).toString()
    });
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      per_page: newRowsPerPage.toString(),
      page: '1'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  // Initialize from URL params
  useEffect(() => {
    if (mounted) return;
    setMounted(true);

    // Initialize from URL params
    const pageParam = searchParams.get('page');
    const perPageParam = searchParams.get('per_page');
    const statusParam = searchParams.get('status');
    const typeParam = searchParams.get('transaction_type');

    if (pageParam) {
      setPage(parseInt(pageParam, 10) - 1);
    }
    if (perPageParam) {
      setRowsPerPage(parseInt(perPageParam, 10));
    }
    if (statusParam) {
      setSelectedStatus(statusParam);
    }
    if (typeParam) {
      setSelectedType(typeParam);
    }

  }, [mounted, searchParams]);

  // Fetch data when filters or pagination changes
  useEffect(() => {
    // Only fetch if component is mounted
    if (mounted) {
      fetchData();
    }
  }, [page, rowsPerPage, selectedStatus, selectedType, mounted]);

  if (userRole !== 'ADMIN') {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      <Box sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/bank-transactions/import')}
            >
              Nhập dữ liệu
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Trạng thái</InputLabel>
            <Select
              value={selectedStatus}
              label="Trạng thái"
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  status: e.target.value,
                  page: '1'
                });
                setPage(0);
              }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              {Object.entries(bankTransactionStatusTranslations).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Loại giao dịch</InputLabel>
            <Select
              value={selectedType}
              label="Loại giao dịch"
              onChange={(e) => {
                setSelectedType(e.target.value);
                setSearchParams({
                  ...Object.fromEntries(searchParams),
                  transaction_type: e.target.value,
                  page: '1'
                });
                setPage(0);
              }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              {Object.entries(bankTransactionTypeTranslations).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} sx={{ overflow: 'auto' }}>
          <Table sx={{ minWidth: 650, tableLayout: 'fixed' }} aria-label="bank transactions table">
            <TableHead>
              <TableRow>
                <TableCell width="15%">Mã giao dịch</TableCell>
                <TableCell width="15%">Ngày giao dịch</TableCell>
                <TableCell width="12%">Số tiền</TableCell>
                <TableCell width="12%">Số dư</TableCell>
                <TableCell width="12%">Loại giao dịch</TableCell>
                <TableCell width="12%">Trạng thái</TableCell>
                <TableCell width="15%">Mô tả</TableCell>
                <TableCell width="7%">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : bankTransactions.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Không có dữ liệu
                    </TableCell>
                  </TableRow>
                ) : (
                    bankTransactions.items.map((transaction) => (
                      <TableRow key={transaction.id || Math.random()} hover>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {transaction.code || '-'}
                        </TableCell>
                        <TableCell>
                          {transaction.happened_at ? dayjs(transaction.happened_at).format('DD/MM/YYYY HH:mm') : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              color: transaction.amount > 0 ? 'success.main' : 'error.main',
                              fontWeight: 'bold'
                            }}
                          >
                            {formatCurrency(transaction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatCurrency(transaction.current_balance)}</TableCell>
                        <TableCell>
                          <Chip
                            label={bankTransactionTypeTranslations[transaction.transaction_type] || transaction.transaction_type || '-'}
                            color={getBankTransactionTypeColor(transaction.transaction_type)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={bankTransactionStatusTranslations[transaction.status] || transaction.status || '-'}
                            color={getBankTransactionStatusColor(transaction.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {transaction.description || '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/bank-transactions/${transaction.id}`)}
                            disabled={!transaction.id}
                          >
                            Chi tiết
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={bankTransactions.total || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số hàng mỗi trang:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
        />
      </Box>
    </Container >
  );
});

export default BankTransactionsList;
